import React, { useMemo, useState } from "react";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import SearchIcon from "@mui/icons-material/Search";
import { dashboardInputClassName } from "../../common/dashboard/formStyles";
import {
  REPORT_DEFINITIONS,
  ReportDefinition,
} from "./reportDefinitions";

export type ReportSelectorProps = {
  disabled?: boolean;
  selectedReport: ReportDefinition | null;
  onChange: (report: ReportDefinition) => void;
};

const ReportSelector = ({
  disabled = false,
  selectedReport,
  onChange,
}: ReportSelectorProps) => {
  const [searchQuery, setSearchQuery] = useState("");
  const filteredReports = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();

    return normalizedQuery
      ? REPORT_DEFINITIONS.filter((report) =>
          report.title.toLowerCase().includes(normalizedQuery),
        )
      : REPORT_DEFINITIONS;
  }, [searchQuery]);

  return (
    <div className="min-w-0">
      <label
        htmlFor="general-report-search"
        className="mb-2 block text-sm font-bold text-gray-950"
      >
        Select General Report
      </label>
      <div className="relative">
        <SearchIcon
          aria-hidden="true"
          sx={{
            color: "#8A9AAF",
            fontSize: 21,
            left: 14,
            pointerEvents: "none",
            position: "absolute",
            top: "50%",
            transform: "translateY(-50%)",
          }}
        />
        <input
          id="general-report-search"
          type="text"
          role="searchbox"
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
          placeholder="Search Reports..."
          disabled={disabled}
          className={`${dashboardInputClassName} h-[58px] w-full pl-11 pr-11 text-[15px]`}
          aria-controls="general-report-options"
        />
        <KeyboardArrowUpIcon
          aria-hidden="true"
          sx={{
            color: "#8A9AAF",
            fontSize: 20,
            pointerEvents: "none",
            position: "absolute",
            right: 14,
            top: "50%",
            transform: "translateY(-50%)",
          }}
        />
      </div>

      <div
        id="general-report-options"
        role="group"
        aria-label="General reports"
        className="mt-6 max-h-[1000px] overflow-y-auto rounded-md border border-[#DCE4F0] bg-white shadow-[0_8px_20px_rgba(15,23,42,0.12)]"
      >
        {filteredReports.length > 0 ? (
          filteredReports.map((report) => {
            const selected = report.id === selectedReport?.id;

            return (
              <button
                key={report.id}
                type="button"
                aria-pressed={selected}
                disabled={disabled}
                onClick={() => onChange(report)}
                className={`block min-h-[51px] w-full border-b border-[#E5EAF2] px-5 py-[13px] text-left text-[15px] leading-[1.45] transition last:border-b-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-cpaGold disabled:cursor-not-allowed ${
                  selected
                    ? "bg-[#E1E5EA] text-[#182237]"
                    : "bg-white text-[#53657D] hover:bg-gray-50"
                }`}
              >
                {report.number}. {report.title}
              </button>
            );
          })
        ) : (
          <p
            className="px-5 py-6 text-sm text-gray-600"
            role="status"
            aria-live="polite"
          >
            No reports match your search.
          </p>
        )}
      </div>
    </div>
  );
};

export default ReportSelector;
